import { type Mutation, Query, type QueryCache } from '@tanstack/react-query';
import { denormalize, normalize, schema as normalizrSchema, type Schema, schema } from 'normalizr';
import { proxy, subscribe, unstable_enableOp } from 'valtio';
import type { J5ListV1Filter, J5ListV1QueryRequest } from '@interxfi/types';
import { isDeepEqual } from '@interxfi/utilities/equality/deep';
import { debounce } from '@interxfi/utilities/fn/debounce';
import { get } from '@interxfi/utilities/object/get';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { NORMALIZR_ENTITY_REGISTRY } from '../generated/entities/registry';
import { ENTITY_EVENT_QUERY_INVALIDATION_SIDE_EFFECT_REGISTRY } from './side-effects';
import type { IDType, NormalizedEntity, PSMEvent, PSMEventEntity, PSMState } from './types';

// This is required to ensure that updates are pushed to useQuery/useInfiniteQuery observers
unstable_enableOp(true);

function removeTrailingType(str: string): string {
  return str.replace(/Type$/, '');
}

export class NormalizationEntityCache {
  static getIsInfiniteQuery(query: Query | Mutation) {
    return query instanceof Query && query.options && 'getNextPageParam' in query.options;
  }

  static getNormalizationSchema(queryOrMutation: Query | Mutation) {
    const base = queryOrMutation.options.meta?.normalizationSchema as Schema | undefined;

    if (!base) {
      return undefined;
    }

    const isInfiniteQuery = NormalizationEntityCache.getIsInfiniteQuery(queryOrMutation);
    return isInfiniteQuery ? new normalizrSchema.Object({ pages: [base] }) : base;
  }

  normalizedResponses: Record<string, NormalizedEntity> = proxy({});
  entities: Record<string, Record<string, NormalizedEntity>> = proxy({});
  entityDependencies: Record<string, Record<string, Set<Query>>> = {};
  private _pendingQueryResets: Set<Query> = new Set();

  constructor() {
    this.invalidateAndResetQueries = this.invalidateAndResetQueries.bind(this);
    this.initialize();
  }

  private initialize() {
    this.normalizedResponses = proxy({});
    this.entities = proxy({});
    this.entityDependencies = proxy({});

    subscribe(this.entities, (ops) => {
      const updateTime = Date.now();

      for (const op of ops) {
        const [opName, path] = op;

        if (opName === 'set' || opName === 'delete') {
          const [entityName, entityId] = path;

          const queries = this.entityDependencies[entityName as string]?.[entityId as string];

          if (queries?.size) {
            for (const query of queries.values()) {
              const normalizationSchema = NormalizationEntityCache.getNormalizationSchema(query);

              if (normalizationSchema) {
                const denormalized = denormalize(this.normalizedResponses[query.queryHash], normalizationSchema, this.entities);

                if (!isDeepEqual(query.state.data, denormalized)) {
                  query.setData(denormalized, { updatedAt: updateTime, manual: true });
                }
              }
            }
          }
        }
      }
    });
  }

  public reset() {
    this.initialize();
  }

  removeQuery(query: Query) {
    for (const [entityName, entities] of Object.entries(this.entityDependencies)) {
      for (const [entityId, queries] of Object.entries(entities)) {
        queries.delete(query);

        if (queries.size === 0) {
          delete this.entityDependencies[entityName][entityId];
          delete this.entities[entityName][entityId];
        }
      }
    }
  }

  private findEntityTimestamp(data: NormalizedEntity) {
    const strValue = (get(data, 'metadata.updatedAt') || get(data, 'metadata.timestamp')) as string | undefined;
    if (!strValue) {
      return undefined;
    }

    try {
      return new Date(strValue);
    } catch {
      return undefined;
    }
  }

  private addEntities(normalizedEntities: Record<string, Record<string, NormalizedEntity>>, query?: Query) {
    for (const [entityName, entities] of Object.entries(normalizedEntities)) {
      if (!this.entities[entityName]) {
        this.entities[entityName] = proxy({});
      }

      // Add the entities for the response to the entity and entity dependency caches
      for (const [id, entity] of Object.entries(entities || {})) {
        if (query) {
          this.addEntityDependency(query, entityName, id);
        }

        if (!this.entities[entityName][id] || !isDeepEqual(this.entities[entityName][id], entity)) {
          const existingTimestamp = this.findEntityTimestamp(this.entities[entityName][id]);
          const newTimestamp = this.findEntityTimestamp(entity);

          // Existing entity is newer than the incoming one, skip updating
          if (existingTimestamp && newTimestamp && existingTimestamp > newTimestamp) {
            continue;
          }

          this.entities[entityName][id] = entity;
        }
      }
    }
  }

  deleteEntity<T = IDType>(entity: normalizrSchema.Entity, entityId: T) {
    const entityIdAsIdType = entityId as IDType;
    const dependentQueries = this.entityDependencies[entity.key]?.[entityIdAsIdType];

    // If there are dependent queries, we need to remove the entity from the normalized response cache
    if (dependentQueries?.size) {
      let modifiedAQuery = false;

      for (const query of dependentQueries.values()) {
        if (this.normalizedResponses[query.queryHash]) {
          function removeEntityFromObject(response: NormalizedEntity<any, any, any>, parentSchema?: Schema): NormalizedEntity<any, any, any> {
            if (!response) {
              return response;
            }

            if (Array.isArray(response)) {
              return response.reduce((acc, curr) => {
                if (typeof curr === 'object') {
                  acc.push(removeEntityFromObject(curr, parentSchema));
                  return acc;
                }

                if (curr === entityId && parentSchema) {
                  modifiedAQuery = true;
                  return acc;
                }

                acc.push(curr);

                return acc;
              }, []);
            }

            if (typeof response === 'object') {
              return Object.entries(response).reduce<NormalizedEntity<any, any, any>>((acc, [key, curr]) => {
                const baseCurrentKeySchema = (parentSchema as any)?.schema?.[key];
                const currentKeySchema = baseCurrentKeySchema && Array.isArray(baseCurrentKeySchema) ? baseCurrentKeySchema[0] : baseCurrentKeySchema;

                if (curr === entityId && currentKeySchema) {
                  modifiedAQuery = true;

                  return acc;
                }

                if (typeof curr === 'object' && currentKeySchema) {
                  acc[key] = removeEntityFromObject(curr, currentKeySchema);
                  return acc;
                }

                acc[key] = curr;
                return acc;
              }, {});
            }

            return response;
          }

          if (this.normalizedResponses[query.queryHash] === entityId) {
            delete this.normalizedResponses[query.queryHash];

            if (!dependentQueries.size) {
              delete this.entityDependencies[entity.key][entityIdAsIdType];
            }
          } else {
            const schemaToUse = NormalizationEntityCache.getNormalizationSchema(query);
            const newResponse = removeEntityFromObject(JSON.parse(JSON.stringify(this.normalizedResponses[query.queryHash])), schemaToUse);

            if (modifiedAQuery) {
              this.normalizedResponses[query.queryHash] = newResponse;
            }
          }
        }
      }
    } else if (this.entityDependencies[entity.key]) {
      // If there are no dependent queries, we can just delete the entity dependencies straight away
      delete this.entityDependencies[entity.key][entityIdAsIdType];
    }

    // Finally, delete the entity from the "entities" cache. Note that if there are dependent queries for the entity,
    // we aren't deleting the entityDependencies yet, as those will be deleted when the last dependent query is removed.
    // See the subscription callback in addQueryNotifier.
    delete this.entities[entity.key][entityIdAsIdType];
  }

  private addEntityDependency<T = IDType>(query: Query, entityName: string, entityId: T) {
    const entityIdAsIdType = entityId as IDType;

    if (!this.entityDependencies[entityName]) {
      this.entityDependencies[entityName] = {};
    }

    if (!this.entityDependencies[entityName][entityIdAsIdType]) {
      this.entityDependencies[entityName][entityIdAsIdType] = new Set();
    }

    this.entityDependencies[entityName][entityIdAsIdType].add(query);
  }

  preloadData<Data, DataKey extends keyof Data | undefined = undefined>(
    schema: Schema,
    preloadData: NormalizedEntity<Data, DataKey>,
    isInfiniteQuery?: boolean,
  ): Data | undefined {
    if (!preloadData) {
      return undefined;
    }

    const preloaded = denormalize(preloadData, isInfiniteQuery ? new normalizrSchema.Object({ pages: [schema] }) : schema, this.entities) as Data;

    if (!preloaded || isDeepEqual(preloaded, preloadData as any) || Object.values(preloaded).every((value) => value === undefined)) {
      return undefined;
    }

    return preloaded;
  }

  processEventData<Data>(query: Query | Mutation, data: Data) {
    const schemaToUse = NormalizationEntityCache.getNormalizationSchema(query);

    // If no normalization schema is provided, no normalization will occur and everything will behave as with the default react-query QueryCache
    if (schemaToUse && data) {
      const normalizedData = normalize(data, schemaToUse);

      if (query instanceof Query) {
        // Store the normalized response
        this.normalizedResponses[query.queryHash] = normalizedData.result;
      }

      this.addEntities(normalizedData.entities as Record<string, Record<string, NormalizedEntity>>, query instanceof Query ? query : undefined);
    }
  }

  private findRelevantQueries(entity: normalizrSchema.Entity): Set<Query> {
    const queries = new Set<Query<unknown>>();

    for (const q in this.entityDependencies[entity.key] || []) {
      if (this.entityDependencies[entity.key][q] instanceof Set) {
        for (const query of this.entityDependencies[entity.key][q]) {
          const isInfiniteQuery = NormalizationEntityCache.getIsInfiniteQuery(query);

          if (isInfiniteQuery) {
            queries.add(query);
          }
        }
      }
    }

    return queries;
  }

  private updatePSMEntity<TStateData = any, TKeys extends Record<string, string> = {}>(
    entity: normalizrSchema.Entity,
    data: PSMState<TStateData, TKeys> | PSMEventEntity<any, TKeys>,
  ) {
    if (this.entities[entity.key]) {
      const entityId = entity.getId(data, {}, '');

      if (entityId && this.entities[entity.key][entityId]) {
        const normalizedData = normalize(data, entity);
        this.addEntities(normalizedData.entities as Record<string, Record<string, NormalizedEntity>>, undefined);
      }
    }
  }

  private findListifyQuery(query: Query) {
    if (NormalizationEntityCache.getIsInfiniteQuery(query)) {
      if (query.queryKey && Array.isArray(query.queryKey)) {
        for (const part of query.queryKey) {
          if (typeof part === 'object' && 'query' in part && ('sorts' in part.query || 'filters' in part.query || 'searches' in part.query)) {
            return part as J5ListV1QueryRequest;
          }
        }
      }
    }

    return undefined;
  }

  private findEntityEventListQueries<TStateData = any, TKeys extends Record<string, string> = {}>(
    eventEntity: normalizrSchema.Entity,
    entity: normalizrSchema.Entity,
    state: PSMState<TStateData, TKeys>,
  ): Set<Query> {
    const representativeEntityKey = entity.getId(state, {}, '');
    const queries = this.findRelevantQueries(eventEntity);
    const relevantQueries = new Set<Query>();

    for (const q of queries) {
      const isInfiniteQuery = NormalizationEntityCache.getIsInfiniteQuery(q);

      if (isInfiniteQuery) {
        let isForStateEntity = false;

        for (const part of q.queryKey) {
          if (typeof part === 'object') {
            try {
              if (entity.getId(part, {}, '') === representativeEntityKey) {
                isForStateEntity = true;
                break;
              }
            } catch {}
          }
        }

        if (isForStateEntity) {
          relevantQueries.add(q);
        }
      }
    }

    return relevantQueries;
  }

  private applySorts(psmQuery: J5ListV1QueryRequest<any, any, any> | undefined, data: PSMState | PSMEventEntity): (typeof data)[] {
    const sorts = !psmQuery?.sorts?.length
      ? [
          {
            field: 'metadata.timestamp',
            descending: true,
          },
          {
            field: 'metadata.createdAt',
            descending: true,
          },
          {
            field: 'metadata.updatedAt',
          },
        ]
      : psmQuery.sorts;

    return [...data].sort((a, b) => {
      for (const { field, descending } of sorts) {
        if (!field) {
          continue;
        }

        const aVal = get(a, field);
        const bVal = get(b, field);

        if (!aVal && !bVal) {
          continue;
        }

        if (!aVal) {
          return descending ? 1 : -1;
        }

        if (!bVal) {
          return descending ? -1 : 1;
        }

        // Compare
        if (aVal < bVal) {
          return descending ? 1 : -1;
        }

        if (aVal > bVal) {
          return descending ? -1 : 1;
        }
        // If equal, keep going to next sort
      }

      return 0;
    });
  }

  private testQueryMatch(psmQuery: J5ListV1QueryRequest<any, any, any>, state: PSMState | PSMEventEntity): 'invalidate' | boolean {
    if (psmQuery.searches?.length) {
      return 'invalidate';
    }

    if (!psmQuery.filters?.length) {
      return true;
    }

    function checkFilter(filter: J5ListV1Filter<any>): boolean | 'invalidate' {
      if ('field' in filter) {
        if (!filter.field.name) {
          return true;
        }

        const fieldValue = get(state, filter.field.name);

        if ('value' in filter.field.type) {
          return fieldValue === filter.field.type.value;
        }

        if ('in' in filter.field.type) {
          return Boolean(Array.isArray(filter.field.type.in) && filter.field.type.in.includes(fieldValue));
        }

        if ('range' in filter.field.type) {
          let passMax = true;
          let passMin = true;

          if (filter.field.type.range.max) {
            passMax = fieldValue <= filter.field.type.range.max;
          }

          if (filter.field.type.range.min) {
            passMin = fieldValue >= filter.field.type.range.min;
          }

          return passMax && passMin;
        }

        exhaustiveCheck(filter.field.type['!type']);

        return 'invalidate';
      }

      if ('and' in filter) {
        if (!filter.and.filters?.length) {
          return true;
        }

        for (const f of filter.and.filters) {
          const result = checkFilter(f);
          if (result === 'invalidate') {
            return 'invalidate';
          }

          if (!result) {
            return false;
          }
        }

        return true;
      }

      if ('or' in filter) {
        if (!filter.or.filters?.length) {
          return true;
        }

        for (const f of filter.or.filters) {
          const result = checkFilter(f);
          if (result === 'invalidate') {
            return 'invalidate';
          }

          if (result) {
            return true;
          }
        }

        return false;
      }

      exhaustiveCheck(filter['!type']);
      return true;
    }

    for (const filter of psmQuery.filters) {
      const result = checkFilter(filter);

      if (result === 'invalidate' || !result) {
        return result;
      }
    }

    return true;
  }

  private handleQueryUpdate<TStateData = any, TKeys extends Record<string, string> = {}>(
    query: Query,
    entity: normalizrSchema.Entity,
    data: PSMState<TStateData, TKeys> | PSMEventEntity<any, TKeys>,
  ) {
    const querySchema = NormalizationEntityCache.getNormalizationSchema(query);
    if (!querySchema) {
      return undefined;
    }

    // Narrow to ensure it's an infinite query with a pages array of objects with schemas
    if (
      !querySchema ||
      !('schema' in querySchema) ||
      !('pages' in querySchema.schema) ||
      !Array.isArray(querySchema.schema.pages) ||
      !('schema' in querySchema.schema.pages[0])
    ) {
      return undefined;
    }

    const dataKey = Object.entries(querySchema.schema.pages[0].schema || {})?.find(
      ([_, v]) => Array.isArray(v) && v.length === 1 && 'key' in v[0] && v[0].key === entity.key,
    )?.[0];

    if (!dataKey) {
      return undefined;
    }

    const denormalized = denormalize(this.normalizedResponses[query.queryHash], querySchema, this.entities);

    if ('pages' in denormalized) {
      if (denormalized.pages === undefined) {
        this._pendingQueryResets.add(query);
        return;
      }

      if (Array.isArray(denormalized.pages)) {
        if (!denormalized.pages.some(Boolean)) {
          this._pendingQueryResets.add(query);
          return;
        }

        const flatData: (typeof data)[] = [];
        let maxPageSize = 0;

        for (let i = 0; i < denormalized.pages.length; i += 1) {
          const page = denormalized.pages[i];

          if (typeof page === 'object' && dataKey in page && page[dataKey] && Array.isArray(page[dataKey])) {
            const pageData = page[dataKey] as PSMState<TStateData, TKeys>[];

            if (pageData) {
              flatData.push(...pageData);
              maxPageSize = Math.max(maxPageSize, pageData.length);
            }
          }
        }

        // If the entity already exists in the data, we can skip further processing
        if (flatData.some((d) => entity.getId(d, {}, '') === entity.getId(data, {}, ''))) {
          return;
        }

        flatData.unshift(data);

        const listifyQuery = this.findListifyQuery(query);

        // Test the query against filters to see if the data fits
        const matchResult = !listifyQuery || this.testQueryMatch(listifyQuery, data);
        switch (matchResult) {
          case 'invalidate':
            this._pendingQueryResets.add(query);
            return;
          case false:
            return;
          case true: {
            // Either invalidate or update the query data in place down below
            break;
          }
        }

        const sortedFlatData = this.applySorts(listifyQuery, flatData);

        // Batch the data back into pages of the same size as before
        const newPages: typeof denormalized.pages = [];
        for (let i = 0; i < sortedFlatData.length; i += maxPageSize) {
          const pageSlice = sortedFlatData.slice(i, i + maxPageSize);
          newPages.push({ [dataKey]: pageSlice });
        }

        if (!isDeepEqual(denormalized.pages, newPages)) {
          if (denormalized.pages.length !== 1 && denormalized.pages.length !== newPages.length) {
            this._pendingQueryResets.add(query);
          } else {
            this.processEventData(query, { ...denormalized, pages: newPages });
          }
        }
      }
    } else {
      this._pendingQueryResets.add(query);
    }
  }

  public processPSMEvent<TStateData = any, TKeys extends Record<string, string> = {}, TEvent = any>(
    event: PSMEvent<TStateData, TKeys, TEvent>,
    cache: QueryCache,
  ) {
    const entityMatch = NORMALIZR_ENTITY_REGISTRY[event.entityName];
    if (entityMatch) {
      // First, handle the state & event entity update themselves
      const dataMatch: PSMState<TStateData, any> | undefined = this.preloadData(new schema.Object({ value: entityMatch }), {
        value: entityMatch.getId(event.entityKeys.value, {}, ''),
      })?.value;

      const eventEntityMatch = NORMALIZR_ENTITY_REGISTRY[removeTrailingType(event.eventData['!type'])];

      let prospectiveEventData: PSMEventEntity | undefined;
      if (eventEntityMatch) {
        prospectiveEventData = {
          entity: eventEntityMatch,
          data: {
            metadata: {
              eventId: event.id,
              sequence: event.sequence,
              timestamp: event.timestamp,
            },
            ...event.entityKeys.value,
            event: {
              '!type': event.eventType,
              [event.eventType]: event.eventData.value,
            },
          },
        };

        // Update the event entity itself in the normalized cache
        this.updatePSMEntity(eventEntityMatch, prospectiveEventData);
      }

      const prospectiveData: PSMState<TStateData, TKeys> = {
        ...event.entityKeys.value,
        metadata: {
          createdAt: dataMatch?.metadata?.createdAt || event.timestamp,
          lastSequence: event.sequence,
          updatedAt: event.timestamp ?? dataMatch?.metadata?.updatedAt,
        },
        data: event.entityState.value ?? dataMatch.data,
        status: event.entityStatus ?? dataMatch.status,
      };

      // Update the entity itself in the normalized cache
      this.updatePSMEntity(entityMatch, prospectiveData);

      // Find list queries that might need the entity added to it
      const dataQueries = this.findRelevantQueries(entityMatch);

      for (const q of dataQueries) {
        this.handleQueryUpdate(q, entityMatch, prospectiveData);
      }

      // Next, handle updating event queries for the entity
      if (prospectiveEventData) {
        const relevantEventQueries = this.findEntityEventListQueries(eventEntityMatch, entityMatch, prospectiveData);

        for (const q of relevantEventQueries) {
          this.handleQueryUpdate(q, entityMatch, prospectiveEventData);
        }
      } else {
        console.warn(`Unknown event from long-polling event found for ${event.eventData['!type']}`);
      }

      // Then trigger any side effects that may be necessary
      const sideEffects = ENTITY_EVENT_QUERY_INVALIDATION_SIDE_EFFECT_REGISTRY[entityMatch.key];
      if (sideEffects) {
        const affectedQueryKeys = sideEffects(event.entityKeys.value as any);
        const sideEffectQueries: Query[] = [];

        for (const queryKey of affectedQueryKeys) {
          const queries = cache.findAll({ queryKey });
          sideEffectQueries.push(...queries);
        }

        for (const query of sideEffectQueries) {
          this._pendingQueryResets.add(query);
        }
      }
    } else {
      console.warn(`Unknown entity from long-polling event found for ${event.entityName}`);
    }

    this.invalidateAndResetQueries();
  }

  private readonly invalidateAndResetQueries = debounce(() => {
    this._pendingQueryResets.forEach((query) => {
      if (query.state.status !== 'pending') {
        query.invalidate();
        query.fetch();
      }

      this._pendingQueryResets.delete(query);
    });
  }, 250);
}
